/**
 * Generic event subscription system for decoupled communication between stores.
 * Uses Symbols for event keys to guarantee uniqueness and prevent naming collisions.
 */

type EventCallback = (payload?: unknown) => void | Promise<void>;

interface EventBusConfig {
  debug?: boolean;
}

export interface IEventBus<EventKeys extends Record<string, symbol>> {
  emit<K extends keyof EventKeys>(eventName: K, payload?: unknown): Promise<void>;
  subscribe<K extends keyof EventKeys>(eventName: K, callback: EventCallback): () => void;
  once<K extends keyof EventKeys>(eventName: K, callback: EventCallback): () => void;
  unsubscribe<K extends keyof EventKeys>(eventName: K, callback: EventCallback): void;
  clear<K extends keyof EventKeys>(eventName?: K): void;
  getSubscriberCount<K extends keyof EventKeys>(eventName: K): number;
}

/**
 * Create a typed event bus with Symbol-based events for guaranteed uniqueness
 */
export function createEventBus<EventKeys extends Record<string, symbol>>(
  eventKeys: EventKeys,
  config: EventBusConfig = {},
): IEventBus<EventKeys> {
  const subscribers = new Map<symbol, Set<EventCallback>>();
  const debug = config.debug ?? false;

  const log = (message: string, data?: unknown) => {
    if (debug) {
      console.log(`[EventBus] ${message}`, data || '');
    }
  };

  return {
    emit: async (eventName, payload) => {
      const symbol = eventKeys[eventName];
      log(`emit: ${String(eventName)}`, payload);
      const callbacks = subscribers.get(symbol);
      if (!callbacks || callbacks.size === 0) return;

      const promises = Array.from(callbacks).map((callback) =>
        Promise.resolve().then(() => callback(payload)),
      );
      await Promise.all(promises);
    },

    subscribe: (eventName, callback) => {
      const symbol = eventKeys[eventName];
      if (!subscribers.has(symbol)) {
        subscribers.set(symbol, new Set());
      }
      subscribers.get(symbol)!.add(callback);
      return () => subscribers.get(symbol)?.delete(callback);
    },

    once: (eventName, callback) => {
      const symbol = eventKeys[eventName];
      const wrappedCallback: EventCallback = (payload) => {
        subscribers.get(symbol)?.delete(wrappedCallback);
        return callback(payload);
      };
      if (!subscribers.has(symbol)) {
        subscribers.set(symbol, new Set());
      }
      subscribers.get(symbol)!.add(wrappedCallback);
      return () => subscribers.get(symbol)?.delete(wrappedCallback);
    },

    unsubscribe: (eventName, callback) => {
      const symbol = eventKeys[eventName];
      subscribers.get(symbol)?.delete(callback);
    },

    clear: (eventName) => {
      if (eventName) {
        const symbol = eventKeys[eventName];
        subscribers.delete(symbol);
      } else {
        subscribers.clear();
      }
    },

    getSubscriberCount: (eventName) => {
      const symbol = eventKeys[eventName];
      return subscribers.get(symbol)?.size ?? 0;
    },
  };
}
